import { DatePicker } from '@/components/DatePicker'
import { FormDialog } from '@/components/FormDialog'
import { ImageUpload } from '@/components/ImageUpload'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectItemTestId, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Product } from '@/features/equipment/types/product'
import { useGetQuotations } from '@/features/quotations/hooks/useQuotation'
import { useGetVendors } from '@/features/vendors/hooks/useVendors'
import { useFileUploadField } from '@/hooks/useFileUpload'
import { getLocalDateString, toUtcDateString } from '@/utils/date'
import { isApiError } from '@/utils/error'
import { getMutationOptions } from '@/utils/mutation'
import { showError } from '@/utils/toast'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCreateItem, useGetItem, useUpdateItem } from '../hooks/useItem'

type ItemForm = {
  serialNumber: string
  departmentId: string
  vendorId: string
  lotId: string
  tagId: string
  quotationId?: string
  purchaseDate: string
  warrantyExpiry: string
  nextMaintenance: string
  maintenancePerYear: string
  notes: string
  imageIds: string[] | []
}

export function ItemDialog({
  open,
  onOpenChange,
  product,
  itemId,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  product: Product
  itemId?: string | null
}) {
  if (!product) return
  const { t } = useTranslation()
  const { data: vendors = [], isFetching: isLoadingVendors } = useGetVendors(undefined, open)
  const { data: item, isFetching: isLoadingItem } = useGetItem(itemId ?? '', !!itemId && open)
  const { data: quotations = [], isFetching: isLoadingQuote } = useGetQuotations(
    { productId: product.id, status: 'Submitted' },
    !itemId && open,
  )

  const createItem = useCreateItem()
  const updateItem = useUpdateItem()

  const [form, setForm] = useState<ItemForm>({
    serialNumber: '',
    departmentId: '',
    vendorId: '',
    lotId: '',
    tagId: '',
    quotationId: '',
    purchaseDate: '',
    warrantyExpiry: '',
    nextMaintenance: '',
    maintenancePerYear: '1',
    notes: '',
    imageIds: [],
  })
  const isEdit = !!itemId
  const isInvalid =
    !form.serialNumber ||
    !form.purchaseDate ||
    !form.warrantyExpiry ||
    !form.nextMaintenance ||
    !form.maintenancePerYear ||
    !form.vendorId ||
    form.vendorId === 'none'
  const isLoading = isLoadingItem || createItem.isPending || updateItem.isPending

  const filteredQuotations = quotations?.filter(q => new Date(q.validUntil) >= new Date())

  const imageField = useFileUploadField({
    value: form.imageIds,
    maxFiles: 5,
    onChange: ids =>
      setForm(prev => {
        const same = prev.imageIds.length === ids.length && prev.imageIds.every((x, i) => x === ids[i])

        if (same) return prev

        return {
          ...prev,
          imageIds: ids,
        }
      }),
    onUploadError: error => {
      if (isApiError(error)) {
        showError(error.message)
      }
    },
  })

  useEffect(() => {
    if (item) {
      setForm({
        serialNumber: item.serialNumber,
        lotId: item.lotId ?? '',
        departmentId: item.departmentId ?? '',
        vendorId: item.vendorId ?? '',
        tagId: item.tagId ?? '',
        // quotationId: item.quotationId ?? '',
        purchaseDate: item.purchaseDate ?? '',
        warrantyExpiry: item.warrantyExpiry ?? '',
        nextMaintenance: item.nextMaintenance ?? '',
        maintenancePerYear: item.maintenancePerYear?.toString() ?? '',
        notes: item.notes ?? '',
        imageIds: item.imageIds ?? [],
      })
    } else {
      setForm({
        serialNumber: '',
        lotId: getLocalDateString(),
        departmentId: '',
        vendorId: '',
        tagId: '',
        // quotationId: '',
        purchaseDate: '',
        warrantyExpiry: '',
        nextMaintenance: '',
        maintenancePerYear: '',
        notes: '',
        imageIds: [],
      })
    }
  }, [item, open])

  useEffect(() => {
    if (!form.quotationId || !form.purchaseDate) return

    const quotation = filteredQuotations.find(q => q.id === form.quotationId)
    if (!quotation) return

    const warrantyExpiry = new Date(form.purchaseDate)
    warrantyExpiry.setFullYear(warrantyExpiry.getFullYear() + quotation.warrantyYears)
    const nextValue = warrantyExpiry.toISOString().split('T')[0]

    if (form.warrantyExpiry !== nextValue) {
      setForm(prev => ({
        ...prev,
        warrantyExpiry: nextValue,
      }))
    }
  }, [form.quotationId, form.purchaseDate])

  const formatPrice = (price: number) => new Intl.NumberFormat('th-TH').format(price)

  // When quotation is selected, auto-fill vendor
  function handleQuotationChange(quotationId: string) {
    if (isEdit) return
    if (quotationId === 'none') {
      setForm({ ...form, quotationId: '', vendorId: '', warrantyExpiry: '' })
      return
    }
    const quotation = filteredQuotations.find(q => q.id === quotationId)
    if (quotation) {
      setForm({
        ...form,
        quotationId,
        vendorId: quotation.vendorId,
      })
    }
  }

  function handleSubmit() {
    if (isInvalid) return

    const options = getMutationOptions({
      successMessage: isEdit ? t('equipment.item.toast.editSuccess') : t('equipment.item.toast.addSuccess'),
      onSuccess: () => {
        onOpenChange(false)
      },
    })

    const payload = {
      ...form,
      purchaseDate: form.purchaseDate ? toUtcDateString(form.purchaseDate) : null,
      warrantyExpiry: form.warrantyExpiry ? toUtcDateString(form.warrantyExpiry) : null,
      nextMaintenance: form.nextMaintenance ? toUtcDateString(form.nextMaintenance) : null,
    }

    if (isEdit && item) {
      updateItem.mutate({ id: item.id, payload: payload }, options)
    } else {
      const createPayload = {
        ...payload,
        quotationId: form.quotationId ?? null,
        departmentId: product.ownerDepartmentId,
      }
      createItem.mutate({ productId: product.id, payload: createPayload }, options)
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? t('equipment.item.edit') : t('equipment.item.add')}
      onSubmit={handleSubmit}
      isLoading={isLoading}
      isInvalid={isInvalid}
      contentTestId="item-dialog"
      submitTestId="item-form-submit"
    >
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('equipment.item.serialNumber')} *</Label>
          <Input
            data-testid="item-form-serial"
            value={form.serialNumber}
            onChange={e => setForm({ ...form, serialNumber: e.target.value })}
            className="h-9 bg-secondary/50 text-xs font-mono"
            placeholder=""
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('equipment.item.lotId')}</Label>
          <Input
            data-testid="item-form-lot"
            value={form.lotId}
            onChange={e => setForm({ ...form, lotId: e.target.value })}
            className="h-9 bg-secondary/50 text-xs font-mono"
            placeholder=""
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('equipment.item.department')} *</Label>
          <Input
            data-testid="item-form-department"
            value={product.ownerDepartmentName}
            disabled
            className="h-9 bg-secondary/50 text-xs"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('equipment.item.tagId')}</Label>
          <Input
            data-testid="item-form-tag"
            value={form.tagId}
            onChange={e => setForm({ ...form, tagId: e.target.value })}
            className="h-9 bg-secondary/50 text-xs font-mono"
            placeholder=""
          />
        </div>
      </div>
      {!isEdit && filteredQuotations.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('equipment.item.quotation')}</Label>
          <Select value={form.quotationId || 'none'} onValueChange={handleQuotationChange}>
            <SelectTrigger
              data-testid="item-form-quotation"
              className="h-9 bg-secondary/50 text-xs w-full"
              disabled={isLoadingQuote}
            >
              <SelectValue placeholder={t('common.placeholder.selectQuotation')} />
            </SelectTrigger>
            <SelectContent className="bg-card text-foreground">
              <SelectItem value="none">- {t('common.none')} -</SelectItem>
              {filteredQuotations.map(q => {
                const vendor = vendors.find(v => v.id === q.vendorId)
                return (
                  <SelectItemTestId key={q.id} value={q.id} testId={`item-form-quotation-option-${q.id}`}>
                    {vendor?.name} - {formatPrice(q.price)} {t('common.baht')}{' '}
                    {t('equipment.item.warrantyYear', { count: q.warrantyYears })}
                  </SelectItemTestId>
                )
              })}
            </SelectContent>
          </Select>
          {form.quotationId && (
            <p className="text-[10px] text-muted-foreground">* {t('equipment.item.quotationAutoFill')}</p>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">
            {t('common.vendor')} *
            {form.quotationId && <span className="text-[9px] text-primary">{t('equipment.item.fromQuotation')}</span>}
          </Label>
          <Select
            value={form.vendorId || 'none'}
            onValueChange={v => setForm({ ...form, vendorId: v })}
            disabled={!!form.quotationId || isLoadingVendors || isEdit}
          >
            <SelectTrigger data-testid="item-form-vendor" className="h-9 bg-secondary/50 text-xs w-full">
              <SelectValue placeholder={t('common.placeholder.selectVendor')} />
            </SelectTrigger>
            <SelectContent className="bg-card text-foreground">
              <SelectItem value="none">- {t('common.none')} -</SelectItem>
              {vendors.map(v => (
                <SelectItemTestId key={v.id} value={v.id} testId={`item-form-vendor-option-${v.id}`}>
                  {v.name}
                </SelectItemTestId>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div></div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('equipment.item.purchaseDate')} *</Label>
          <DatePicker
            testId="item-form-purchase-date"
            value={form.purchaseDate}
            onChange={purchaseDate => setForm({ ...form, purchaseDate })}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('equipment.item.warrantyExpire')} *</Label>
          <DatePicker
            testId="item-form-warranty-expiry"
            value={form.warrantyExpiry}
            onChange={warrantyExpiry => setForm({ ...form, warrantyExpiry })}
            disabled={!!form.quotationId}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('equipment.item.nextMa')} *</Label>
          <DatePicker
            testId="item-form-next-maintenance"
            value={form.nextMaintenance}
            onChange={nextMaintenance => setForm({ ...form, nextMaintenance })}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('equipment.item.maPerYear')} *</Label>
          <Input
            data-testid="item-form-maintenance-per-year"
            type="number"
            min="1"
            max="12"
            value={form.maintenancePerYear}
            onChange={e => setForm({ ...form, maintenancePerYear: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
          />
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label className="text-xs">{t('equipment.item.note')}</Label>
        <Textarea
          data-testid="item-form-note"
          value={form.notes}
          onChange={e => setForm({ ...form, notes: e.target.value })}
          className="bg-secondary/50 text-xs"
          rows={2}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label className="text-xs">{t('common.images')}</Label>
        {imageField.isUploading ? (
          <p className="text-[11px] text-muted-foreground">
            {t('common.uploadingImages')}
            {imageField.uploadProgress ? ` ${imageField.uploadProgress}%` : '...'}
          </p>
        ) : null}
        <div>
          <ImageUpload
            testId="item-form-images"
            files={imageField.files}
            imageIds={imageField.imageIds}
            onChange={imageField.handleChange}
            onRemoveExisting={imageField.handleRemoveExisting}
            maxImages={5}
          />
        </div>
      </div>
    </FormDialog>
  )
}
